import React, { Component } from "react";
import "./index.css";
import { GlobalContext } from "./global-context";
import Layout from "./components/Layout";
import Auth0Lock from "auth0-lock";
import ApolloClient from "apollo-client"
import { WebSocketLink } from "apollo-link-ws"
import { HttpLink } from "apollo-link-http"
import { split } from "apollo-link"
import { getMainDefinition } from "apollo-utilities"
import { InMemoryCache } from "apollo-cache-inmemory"
import { setContext } from "apollo-link-context"
import { ApolloProvider } from "react-apollo"
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles'
import { withWidth } from '@material-ui/core'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import axios from 'axios'
import BoothByMail from '@BoothByMail/boothbymail-sdk'

let log = false

const theme = createMuiTheme({
  palette: {
    // Modern Theme Colors
    primary: { light: '#5eb8ff', main: '#1976d2', dark: '#004ba0', contrastText: '#fff' },
    secondary: { light: '#718792', main: '#455a64', dark: '#1c313a', contrastText: '#fff' },
    warning: { light: '#ffcd38', main: '#ffa000', dark: '#c67100', contrastText: '#000' },
    error: { superLight: '#ffebee', light: '#ef5350', main: '#e53935', dark: '#c62828', contrastText: '#fff' },
    success: { light: '#80e27e', main: '#4caf50', dark: '#087f23', contrastText: '#fff' },
    info: { light: '#63a4ff', main: '#1976d2', dark: '#004ba0', contrastText: '#fff' },

    // Text Colors - Darkened for better contrast
    text: {
      primary: '#0d1b24', // Darkened from #263238
      secondary: '#344955', // Darkened from #546e7a
      tertiary: '#546e7a', // Darkened from #78909c
      disabled: '#90a4ae', // Darkened from #b0bec5
      hint: '#90a4ae', // Darkened from #b0bec5
    },

    // Custom Colors - Increased contrast
    custom: {
      light: '#f5f5f5', // Darkened from #fafafa
      medium: '#e0e0e0', // Darkened from #f5f5f5
      dark: '#bdbdbd', // Darkened from #e0e0e0
    },

    // Action Colors
    action: {
      hover: '#e0e0e0', // Darkened from #f5f5f5
      selected: '#bbdefb', // Darkened from #e3f2fd
      disabledBackground: '#cfd8dc', // Darkened from #eceff1
    },

    // Background Colors
    background: {
      default: '#f2f5f8', // Slightly more saturated/contrasting
      paper: '#ffffff',
    },

    // Border/Divider Colors
    divider: '#cfd8dc', // Darkened from #eceff1
  },

  typography: {
    fontFamily: ['Roboto', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Arial', 'sans-serif'].join(','),
    // Increased base font size for better iPad readability
    fontSize: 16, // Default was 14
    h1: {
      fontWeight: 500,
      fontSize: '2.5rem', // Increased from default
    },
    h2: {
      fontWeight: 500,
      fontSize: '2.2rem', // Increased from default
    },
    h3: {
      fontWeight: 500,
      fontSize: '1.9rem', // Increased from default
    },
    h4: {
      fontWeight: 500,
      fontSize: '1.6rem', // Increased from default
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.3rem', // Increased from default
    },
    h6: {
      fontWeight: 500,
      fontSize: '1.1rem', // Increased from default
    },
    body1: {
      fontSize: '1.05rem', // Increased for better readability
    },
    body2: {
      fontSize: '0.95rem', // Increased for better readability
    },
    button: {
      fontSize: '1rem', // Increased for better readability on touch devices
    },
  },

  // Increased general shape sizes for iPad touch targets
  shape: {
    borderRadius: 8,
  },

  overrides: {
    MuiCard: {
      root: {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', // Stronger shadow for better depth
        transition: 'box-shadow 0.3s ease, transform 0.3s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
      },
    },
    MuiButton: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        borderRadius: 8,
        padding: '8px 16px', // Increased padding for touch targets
      },
      contained: {
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        '&:hover': {
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
        },
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: 8,
      },
      elevation1: {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', // Stronger shadow
      },
      elevation2: {
        boxShadow: '0 3px 10px rgba(0, 0, 0, 0.12)', // Stronger shadow
      },
    },
    MuiTableCell: {
      root: {
        padding: '14px 16px', // Increased padding
        fontSize: '1rem', // Larger text in tables
      },
    },
    MuiListItem: {
      root: {
        borderRadius: 8,
        transition: 'background-color 0.2s ease',
        padding: '12px 16px', // Increased padding for touch targets
      },
    },
    MuiAppBar: {
      root: {
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.12)', // Stronger shadow
      },
    },
    // Improve form controls for touch
    MuiCheckbox: {
      root: {
        padding: 10, // Larger touch target
      },
    },
    MuiRadio: {
      root: {
        padding: 10, // Larger touch target
      },
    },
    MuiSwitch: {
      root: {
        padding: '12px 8px', // Larger touch target
      },
    },
    MuiFormLabel: {
      root: {
        fontSize: '1rem', // Larger labels
      },
    },
    MuiInputBase: {
      input: {
        fontSize: '1.05rem', // Larger input text
      },
    },
    // Improve tabs for iPad
    MuiTab: {
      root: {
        fontSize: '0.95rem',
        padding: '12px 16px',
        '@media (min-width: 600px)': {
          minWidth: 100,
        },
      },
    },
  },

  props: {
    MuiButtonBase: {
      disableRipple: false,
    },
  },
});

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      userIsAuthenticated: () => {
        if (this.state.userProfile.email) {
          return true;
        } else return false;
      },
      theme: theme,
      width: props.width,
      notificationShow: false,
      notificationVariant: '',
      notificationMessage: '',
      handleNotifications: async (
        show,
        variant = this.state.notificationVariant,
        message = this.state.notificationMessage
      ) =>
        await this.setState({
          notificationShow: show,
          notificationVariant: variant,
          notificationMessage: message,
        }),
      userProfile: {},
      userToken: '',
      redirectPath: null,
      lock: new Auth0Lock(process.env.REACT_APP_AUTH_CLIENT_ID, process.env.REACT_APP_AUTH_DOMAIN, {
        id: 'login',
        cacheLocation: 'localstorage',
        useRefreshTokens: true,
        allowSignUp: false,
        container: 'login',
        theme: {
          logo: 'https://app.boothbymail.com/assets/img/BoothByMail_logo_monochrome%20[web-white-180].png',
          primaryColor: '#3f51b5',
        },
        languageDictionary: {
          title: '',
        },
        auth: {
          redirectUrl:
            !process.env.NODE_ENV || process.env.NODE_ENV === 'development'
              ? `http://localhost:8888/login`
              : `https://${process.env.REACT_APP_SUBDOMAIN}.boothbymail.com/login`,
          responseType: 'code token id_token',
          params: {
            scope: 'openid email profile', // Learn about scopes: https://auth0.com/docs/scopes
          },
        },
      }),
      logout: () => {
        if (log) {
          console.log('running logout function');
        }
        this.state.lock.logout({
          returnTo:
            !process.env.NODE_ENV || process.env.NODE_ENV === 'development'
              ? `http://localhost:8888/login`
              : `https://${process.env.REACT_APP_A0_CB_SD}.boothbymail.com/login`,
        });
        this.state.userProfile.email = null;
      },
      setUserAuth: (profile, token) => {
        if (log) console.log('profile', profile, 'and token', token);
        profile.bbm_user_id = profile['https://hasura.io/jwt/claims']['x-bbm-user-id'];
        this.setState({ userProfile: profile });
        this.setState({ userToken: token });
      },
      apolloClient: {},
      setupApollo: token => {
        if (log) {
          console.log('Setting up the Apollo...');
        }
        const authLink = setContext(async (_, { headers }) => {
            return {
              headers: {
                ...headers,
                authorization: `Bearer ${token}`,
              },
            };
          }),
          wsurl = process.env.REACT_APP_GRAPHQL_WSS_URL,
          httpurl = process.env.REACT_APP_GRAPHQL_URL,
          wsLink = new WebSocketLink({
            uri: wsurl,
            options: {
              lazy: true,
              reconnect: true,
              timeout: 30000,
              connectionParams: async () => {
                return {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                };
              },
            },
          }),
          httpLink = new HttpLink({
            uri: httpurl,
          }),
          link = split(
            // split based on operation type
            ({ query }) => {
              const { kind, operation } = getMainDefinition(query);
              return kind === 'OperationDefinition' && operation === 'subscription';
            },
            wsLink,
            authLink.concat(httpLink)
          ),
          client = new ApolloClient({
            link,
            cache: new InMemoryCache(),
          });
        if (log) {
          console.log('Apollo Client Initialized! ', client);
        }
        this.setState({ apolloClient: client });
        BoothByMail.configure({ apollo_client: client });
      },
      BoothByMail: BoothByMail,
    };
  }

  render() {
    if (this.state.userIsAuthenticated()) {
      log && console.log('Rendering with ApolloProvider now...');
      log && console.log(this.lock);
      return (
        <MuiThemeProvider theme={theme}>
          <GlobalContext.Provider value={this.state}>
            <ApolloProvider client={this.state.apolloClient}>
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <Layout />
              </MuiPickersUtilsProvider>
            </ApolloProvider>
          </GlobalContext.Provider>
        </MuiThemeProvider>
      );
    } else {
      if (log) {
        console.log('Rendering without ApolloProvider until user is logged in.');
      }
      return (
        <MuiThemeProvider theme={theme}>
          <GlobalContext.Provider value={this.state}>
            <Layout />
          </GlobalContext.Provider>
        </MuiThemeProvider>
      );
    }
  }
}

export default withWidth()(App);
